import type { ReactNode } from 'react'
import { Armchair, ArrowUpRight, BadgeCheck, Banknote, ShoppingBag, Users } from 'lucide-react'
import { useT } from '@/components/visitone/visitone-provider'
import { ModuleShell } from '@/components/today/module-shell'
import { AnimatedNumber } from '@/components/today/animated-number'
import { currencyFormatter, formatNumber } from '@/components/today/format'
import { useTodayModule } from '@/hooks/use-today-module'
import type { TodayModuleProps } from '@/components/today/modules/types'

function Figure({
  icon,
  label,
  value,
  format,
  onOpen,
}: {
  icon: ReactNode
  label: string
  value: number
  format: (value: number) => string
  /** Absent when the caller may not open Insights: the figure is shown but doesn't link. */
  onOpen?: () => void
}) {
  const content = (
    <>
      <span className="flex items-center justify-between text-xs text-fg-muted">
        <span className="inline-flex items-center gap-1.5">
          {icon}
          {label}
        </span>
        {onOpen ? (
          <ArrowUpRight aria-hidden="true" className="size-4 opacity-0 transition-opacity group-hover:opacity-100" />
        ) : null}
      </span>
      <span className="text-2xl font-semibold text-fg-strong">
        <AnimatedNumber value={value} format={format} />
      </span>
    </>
  )
  if (!onOpen) {
    return <div className="flex flex-col gap-1 rounded-md border border-line bg-surface p-4">{content}</div>
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col gap-1 rounded-md border border-line bg-surface p-4 text-left transition-colors hover:border-line-strong hover:bg-surface-hover"
    >
      {content}
    </button>
  )
}

/*
 * Today's numbers always renders (even when every other module is empty) and
 * has no dismiss control (VIS-257 AC20). Each figure links to /insights with
 * the equivalent filter pre-applied, when the caller may open Insights. The
 * figures are not headings.
 */
export function TodaysNumbersModule({ tenantId, venue, headingId, onOpenRoute, canOpenRoute }: TodayModuleProps) {
  const t = useT()
  const open = (route: string) => (canOpenRoute(route) ? () => onOpenRoute(route) : undefined)
  const title = t('today.moduleTitles.numbers')
  const { data, error, isLoading, retry } = useTodayModule('numbers', tenantId, venue)
  const money = currencyFormatter(data?.currency ?? 'GBP')

  return (
    <ModuleShell
      title={title}
      headingId={headingId}
      isLoading={isLoading}
      error={error}
      onRetry={retry}
      loadingRows={2}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <Figure
          icon={<Users aria-hidden="true" className="size-3.5" />}
          label={t('today.numbers.visits')}
          value={data?.visits ?? 0}
          format={formatNumber}
          onOpen={open('/insights?metric=visits')}
        />
        <Figure
          icon={<ShoppingBag aria-hidden="true" className="size-3.5" />}
          label={t('today.numbers.orders')}
          value={data?.orders ?? 0}
          format={formatNumber}
          onOpen={open('/insights?metric=orders')}
        />
        <Figure
          icon={<Banknote aria-hidden="true" className="size-3.5" />}
          label={t('today.numbers.revenue')}
          value={data?.revenue ?? 0}
          format={money}
          onOpen={open('/insights?metric=revenue')}
        />
        <Figure
          icon={<BadgeCheck aria-hidden="true" className="size-3.5" />}
          label={t('today.numbers.memberships')}
          value={data?.memberships ?? 0}
          format={formatNumber}
          onOpen={open('/insights?metric=memberships')}
        />
        <Figure
          icon={<Armchair aria-hidden="true" className="size-3.5" />}
          label={t('today.numbers.tables')}
          value={data?.tables ?? 0}
          format={formatNumber}
          onOpen={open('/insights?metric=tables')}
        />
      </div>
    </ModuleShell>
  )
}
